import React, { useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { X, Save } from 'lucide-react';

const BlockEditorModal = ({ block, onClose, onSave }) => {
  const [content, setContent] = useState(block.content);

  const handleSave = () => {
    onSave(content);
  };

  const renderEditor = () => {
    switch (block.type) {
      case 'hero':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Titre Principal</label>
              <Input
                value={content.title || ''}
                onChange={(e) => setContent({ ...content, title: e.target.value })}
                placeholder="Titre du hero"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Sous-titre</label>
              <Input
                value={content.subtitle || ''}
                onChange={(e) => setContent({ ...content, subtitle: e.target.value })}
                placeholder="Sous-titre"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <ReactQuill
                theme="snow"
                value={content.description || ''}
                onChange={(value) => setContent({ ...content, description: value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Texte du bouton</label>
                <Input
                  value={content.cta_text || ''}
                  onChange={(e) => setContent({ ...content, cta_text: e.target.value })}
                  placeholder="Espace membre"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Lien du bouton</label>
                <Input
                  value={content.cta_url || ''}
                  onChange={(e) => setContent({ ...content, cta_url: e.target.value })}
                  placeholder="/login"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">URL de l'image de fond</label>
              <Input
                value={content.background_image || ''}
                onChange={(e) => setContent({ ...content, background_image: e.target.value })}
                placeholder="/api/cms/media/..."
              />
            </div>
          </div>
        );

      case 'text':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Titre</label>
              <Input
                value={content.title || ''}
                onChange={(e) => setContent({ ...content, title: e.target.value })}
                placeholder="Titre du bloc"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Contenu</label>
              <ReactQuill
                theme="snow"
                value={content.text || ''}
                onChange={(value) => setContent({ ...content, text: value })}
                modules={{
                  toolbar: [
                    [{ 'header': [1, 2, 3, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                    ['link', 'image'],
                    ['clean']
                  ]
                }}
              />
            </div>
          </div>
        );

      case 'image':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">URL de l'image</label>
              <Input
                value={content.image_url || ''}
                onChange={(e) => setContent({ ...content, image_url: e.target.value })}
                placeholder="/api/cms/media/..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Texte alternatif</label>
              <Input
                value={content.alt || ''}
                onChange={(e) => setContent({ ...content, alt: e.target.value })}
                placeholder="Description de l'image"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Légende</label>
              <Input
                value={content.caption || ''}
                onChange={(e) => setContent({ ...content, caption: e.target.value })}
                placeholder="Légende optionnelle"
              />
            </div>
          </div>
        );

      case 'stats':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Statistiques</label>
              {(content.items || []).map((item, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <Input
                    value={item.value}
                    onChange={(e) => {
                      const newItems = [...content.items];
                      newItems[index].value = e.target.value;
                      setContent({ ...content, items: newItems });
                    }}
                    placeholder="Valeur"
                    className="w-32"
                  />
                  <Input
                    value={item.label}
                    onChange={(e) => {
                      const newItems = [...content.items];
                      newItems[index].label = e.target.value;
                      setContent({ ...content, items: newItems });
                    }}
                    placeholder="Libellé"
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const newItems = content.items.filter((_, i) => i !== index);
                      setContent({ ...content, items: newItems });
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setContent({
                    ...content,
                    items: [...(content.items || []), { value: '0', label: 'Nouvelle stat' }]
                  });
                }}
              >
                Ajouter une statistique
              </Button>
            </div>
          </div>
        );

      case 'text_image':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Titre</label>
              <Input
                value={content.title || ''}
                onChange={(e) => setContent({ ...content, title: e.target.value })}
                placeholder="Titre"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Texte</label>
              <ReactQuill
                theme="snow"
                value={content.text || ''}
                onChange={(value) => setContent({ ...content, text: value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">URL de l'image</label>
              <Input
                value={content.image_url || ''}
                onChange={(e) => setContent({ ...content, image_url: e.target.value })}
                placeholder="/api/cms/media/..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Position de l'image</label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={content.image_position || 'right'}
                onChange={(e) => setContent({ ...content, image_position: e.target.value })}
              >
                <option value="left">Gauche</option>
                <option value="right">Droite</option>
              </select>
            </div>
          </div>
        );

      default:
        return (
          <div>
            <p className="text-gray-500">Type de bloc non supporté : {block.type}</p>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Éditer le bloc : {block.name}</CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {renderEditor()}

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button onClick={handleSave}>
              <Save className="h-4 w-4 mr-2" />
              Enregistrer
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BlockEditorModal;